import React, { useState, useEffect } from "react";
import DOMPurify from "dompurify";
import classnames from "classnames";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { addTemplate, addVersionDetails, clearBatch } from "state/reducers/emailBatching";
import { useQuery, useLazyQuery } from "@apollo/react-hooks";
import { QUERY_TEMPLATE, QUERY_ALL_TEMPLATES } from "utils/mutations";
import MarketingButtons from "../utils/components/MarketingButtons";
import MarketingNavigation from "../utils/components/MarketingNavigation";
import EmailMarketingStepper from "../utils/components/email/EmailMarketingStepper";
import MarketingToolTip from "../utils/components/MarketingToolTip";
import MarketingContainer from "../utils/components/MarketingContainer";

const EmailBatchEditor = () => {
  const {
    loading: templatesLoading,
    error: templatesError,
    data: templatesData,
  } = useQuery(QUERY_ALL_TEMPLATES);
  const [pages, setPages] = useState(5);
  const [currentPage, setCurrentPage] = useState(0);
  const [error, setError] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [versionId, setVersionId] = useState("");
  const [templates, setTemplates] = useState([]);
  const [html, setHtml] = useState(null);
  const [htmlVariables, setHtmlVariables] = useState("");
  const dispatch = useDispatch();
  const history = useHistory();
  const [
    fetchTemplate,
    { loading: templateLoading, data: templateData, error: templateError },
  ] = useLazyQuery(QUERY_TEMPLATE, {
    onCompleted: async (res) => {
      const { html_content, ...rest } = (res || {}).template || {};
      if (!html_content) { setError("error loading template"); }
      const cleansedHtml = DOMPurify.sanitize(html_content);
      setHtml(cleansedHtml);
      const variables = cleansedHtml
        .split("{{")
        .map((item) => {
          const secondSplit = item.split("}}");
          if (secondSplit.length === 1) { return null; }
          return secondSplit[0];
        })
        .filter((s) => s && !s.includes("unsubscribe"));

      setHtmlVariables(variables);
      dispatch(
        addVersionDetails({
          variables,
          html: cleansedHtml,
          ...rest,
        }),
      );
    },
    onError: (err) => {
      console.log("err getting templates", err);
    },
  });
  useEffect(() => {
    if (!templatesLoading) { setTemplates((templatesData || {}).templates || []); }
    if (templatesError) { setError(templatesError); }
  }, [templatesLoading]);

  const handleNextPage = () => {
    if (pages !== -1 && !(pages >= templates.length)) {
      setPages((prev) => prev + 5);
      setCurrentPage((prev) => prev + 5);
    }
  };
  const handlePrevPage = () => {
    if (currentPage !== 0 && pages !== -1) {
      setPages((prev) => prev - 5);
      setCurrentPage((prev) => prev - 5);
    }
  };
  const handleMinMaxPage = (max) => {
    if (max) {
      setPages(-1);
      setCurrentPage(0);
    } else {
      setPages(5);
      setCurrentPage(0);
    }
  };
  const handleDiscardChanges = () => {
    dispatch(clearBatch());
    history.push("/marketing");
  };
  return (
    <MarketingContainer>
      <MarketingNavigation>
        <MarketingToolTip>
          <small className="">
            <i className="fas fa-info-circle text-gray-600 pr-2" />
              Try adding emojis
            <i className="fas fa-smile mx-1" />
              or variables
            <i className="fas fa-brackets-curly mx-1" />
          </small>
        </MarketingToolTip>
      </MarketingNavigation>
      <EmailMarketingStepper />
      <MarketingButtons
        prevButton="Discard"
        prevLink="/marketing"
        nextButton="Continue"
        nextLink="/marketing/email/upload"
        required={!templateId || !versionId}
        spaceBetween
        discardChanges={handleDiscardChanges}
      />
      <div className="flex flex-col bg-transparent rounded-lg">
        <p className="font-semibold text-left text-xl my-3 ">
            1. Select email template
        </p>
        <div className="flex flex-col h-full mt-2">
          <div className="rounded-sm border">
            {templates.slice(currentPage, pages).map((template, i) => (
              <div
                key={`t-${i}`}
                className={classnames("bg-white shadow-container border-b")}
              >
                <button
                  type="button"
                  className={classnames(
                    "flex w-full px-4 py-4 outline-0-important rounded-none",
                    {
                      "bg-green-300": templateId == template.id,
                    },
                  )}
                  onClick={async () => {
                    if (templateId !== template.id) {
                      setTemplateId(template.id);
                      const newVersionId = template.versions.find(
                        (t) => t.active,
                      )?.id;
                      setVersionId(newVersionId);
                      setHtml(null);
                      dispatch(addTemplate(template));
                      await fetchTemplate({
                        variables: {
                          templateId: template.id,
                          versionId: newVersionId,
                        },
                      });
                    }
                  }}
                >
                  <p>{template.name}</p>
                </button>
                {templateId === template.id ? (
                  <div className="px-8 pt-4 pb-4">
                    <div className="flex">
                      <p className="pr-1">Version: </p>
                      {template.versions.find((v) => v.id === versionId).name}
                    </div>
                    <div className="pt-2 flex">
                      <p className="pr-2">Variables used:</p>
                      {html ? (
                        <p className="font-bold">
                          {htmlVariables.length
                            ? htmlVariables.join(", ")
                            : "None"}
                        </p>
                      ) : (
                        <p>Loading...</p>
                      )}
                    </div>
                    {html ? (
                      <div className="pt-2">
                        <div
                          dangerouslySetInnerHTML={{
                            __html: html,
                          }}
                        />
                      </div>
                    ) : (
                      <p>Loading Preview...</p>
                    )}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-between ">
          <span>
            <button
              className="bg-transparent hover:bg-red-500 text-red-700 font-semibold hover:text-white py-2 px-4 border border-red-500 hover:border-transparent rounded mr-2 my-2"
              onClick={handlePrevPage}
            >
                prev page
            </button>
            <button
              className="bg-transparent hover:bg-blue-500 text-blue-700 font-semibold hover:text-white py-2 px-4 border border-blue-500 hover:border-transparent rounded mr-2 my-2"
              onClick={handleNextPage}
            >
                next page
            </button>
          </span>
          <button
            onClick={() => handleMinMaxPage(pages !== -1)}
          >
            {pages === -1 ? "view less" : "view all"}
          </button>
        </div>
      </div>
    </MarketingContainer>
  );
};

export default EmailBatchEditor;
